import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './servicos_byancasilvaa_.module.scss';
import Logo from './src/perfil.png';

type Servico = {
    id: string;
    title: string;
    description: string;
    images: string[];
    whatsappMessage: string;
};

const SERVICOS_BYANCA_WHATSAPP_NUMBER = '5517992613840';

const buildServicoWhatsAppUrl = (number: string, message: string) =>
    `https://wa.me/${number}?text=${encodeURIComponent(message)}`;

/** Pausa fixa (s) após chegar no fim da imagem e após resetar ao topo — igual para todas as imagens */
const PAN_PAUSE_BOTTOM_SEC = 1.5;
const PAN_PAUSE_TOP_SEC = 1.5;

/** Velocidade constante da rolagem (px/s). Duração da rolagem = panPx / SCROLL_SPEED_PX_PER_SEC */
const SCROLL_SPEED_PX_PER_SEC = 100;

function sanitizeKeyframeName(slideKey: string) {
    return String(slideKey).replace(/[^a-zA-Z0-9_-]/g, '_');
}

/** Keyframes com % do tempo total: rolagem (velocidade fixa) + pausas fixas (repete). */
function buildPanAnimationKeyframes(
    slideKey: string,
    panPx: number
): { name: string; totalSec: number; css: string } | null {
    if (panPx <= 0) return null;
    const tScroll = panPx / SCROLL_SPEED_PX_PER_SEC;
    const totalSec = tScroll + PAN_PAUSE_BOTTOM_SEC + PAN_PAUSE_TOP_SEC;
    const pctScrollEnd = (tScroll / totalSec) * 100;
    const pctAfterBottomPause = ((tScroll + PAN_PAUSE_BOTTOM_SEC) / totalSec) * 100;
    const pctReset = Math.min(pctAfterBottomPause + 0.06, 99.94);
    const name = `byancaPan_${sanitizeKeyframeName(slideKey)}`;
    const css = `@keyframes ${name} {
  0% { transform: translateY(0); }
  ${pctScrollEnd.toFixed(4)}% { transform: translateY(-${panPx}px); }
  ${pctAfterBottomPause.toFixed(4)}% { transform: translateY(-${panPx}px); }
  ${pctReset.toFixed(4)}% { transform: translateY(0); }
  100% { transform: translateY(0); }
}`;
    return { name, totalSec, css };
}

const servicosByanca: Servico[] = [
    {
        id: 'cartao-digital',
        title: 'Cartão Digital (Link na Bio)',
        description:
            'Crie uma página personalizada com seus links principais em um só lugar. Ideal para redes sociais como Instagram, facilitando o acesso dos clientes aos seus serviços, produtos e contatos.',
        images: [
            require('./src/servico_1_1.png'),
            require('./src/servico_1_2.png'),
            require('./src/servico_1_3.png'),
            require('./src/servico_1_4.png'),
            require('./src/servico_1_5.png'),
            require('./src/servico_1_6.png'),
            require('./src/servico_1_7.png'),
            require('./src/servico_1_8.png'),
        ],
        whatsappMessage:
            'Olá! Vim pela página de serviços e quero saber mais sobre o Cartão Digital (Link na Bio).',
    },
    {
        id: 'cardapio-digital',
        title: 'Cardápio Digital',
        description:
            'Perfeito para restaurantes e lanchonetes. Um cardápio online organizado, fácil de acessar e com painel para atualização em tempo real.',
        images: [
            require('./src/servico_2_1.png'),
            require('./src/servico_2_2.png'),
        ],
        whatsappMessage:
            'Olá! Vim pela página de serviços e quero saber mais sobre o Cardápio Digital com painel administrativo.',
    },
    {
        id: 'catalogo-digital',
        title: 'Catálogo Digital',
        description:
            'Tenha um catálogo online completo para seus produtos, com painel administrativo para você gerenciar itens, preços e descrições de forma simples e prática.',
        images: [
            require('./src/servico_3_1.png'),
            require('./src/servico_3_2.png'),
            require('./src/servico_3_3.png'),
        ],
        whatsappMessage:
            'Olá! Vim pela página de serviços e quero saber mais sobre o Catálogo Digital com painel administrativo.',
    },
    {
        id: 'google-meu-negocio',
        title: 'Google Meu Negócio',
        description:
            'Cadastro e otimização da sua empresa no Google para aumentar sua visibilidade, aparecer nas buscas e atrair mais clientes da sua região.',
        images: [
            require('./src/servico_4_1.png'),
            require('./src/servico_4_2.png'),
        ],
        whatsappMessage:
            'Olá! Vim pela página de serviços e quero saber mais sobre Google Meu Negócio.',
    },
    {
        id: 'sites-landing-pages',
        title: 'Sites e Landing Pages',
        description:
            'Desenvolvimento de sites institucionais e landing pages focadas em conversão: layout moderno, responsivo e alinhado à sua marca, para apresentar seu negócio ou campanha com clareza e transformar visitas em contatos ou vendas.',
        images: [
            require('./src/servico_5_1.png'),
            require('./src/servico_5_2.png'),
            require('./src/servico_5_3.png'),
            require('./src/servico_5_4.png'),
            require('./src/servico_5_5.png')
        ],
        whatsappMessage:
            'Olá! Vim pela página de serviços e quero saber mais sobre Sites e Landing Pages.',
    },
];

const Servicos_byancasilvaa_: React.FC = () => {
    const [slideById, setSlideById] = useState<Record<string, number>>({});
    const [panPxByKey, setPanPxByKey] = useState<Record<string, number>>({});
    const [reduceMotion, setReduceMotion] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduceMotion(mq.matches);
        const onChange = () => setReduceMotion(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const measurePan = useCallback((key: string, img: HTMLImageElement) => {
        const slide = img.closest('[data-pan-slide]');
        if (!slide) return;
        requestAnimationFrame(() => {
            const dist = Math.max(0, img.offsetHeight - slide.clientHeight);
            setPanPxByKey((prev) => (prev[key] === dist ? prev : { ...prev, [key]: dist }));
        });
    }, []);

    useEffect(() => {
        const onResize = () => {
            document.querySelectorAll('[data-pan-slide]').forEach((node) => {
                const slide = node as HTMLElement;
                const key = slide.getAttribute('data-pan-key');
                const img = slide.querySelector('img');
                if (!key || !img) return;
                const dist = Math.max(0, img.offsetHeight - slide.clientHeight);
                setPanPxByKey((prev) => ({ ...prev, [key]: dist }));
            });
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const getSlide = (id: string, count: number) => {
        const i = slideById[id] ?? 0;
        return ((i % count) + count) % count;
    };

    const setSlide = useCallback((id: string, count: number, next: number) => {
        setSlideById((prev) => ({
            ...prev,
            [id]: ((next % count) + count) % count,
        }));
    }, []);

    const openService = useCallback((serviceId: string) => {
        setSelectedServiceId(serviceId);
        setSlideById((prev) => ({ ...prev, [serviceId]: 0 }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <div className={styles.servicos_byancasilvaa_}>
            <div className={styles.page}>
                <div className={styles.header}>
                    <Link to="/byancasilvaa_" className={styles.back}>
                        ← Voltar
                    </Link>
                    <h1 className={styles.heading}>Serviços</h1>
                    <p className={styles.subheading}>
                        Soluções digitais para fortalecer sua presença online.
                    </p>
                    <p className={styles.subheading}>
                        Selecione uma opção abaixo para ver detalhes, exemplos e o botão para falar no WhatsApp.
                    </p>
                </div>

                <div className={styles.main}>
                    {!selectedServiceId ? (
                        <div className={styles.menu}>
                            {servicosByanca.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    className={styles.menuButton}
                                    onClick={() => openService(s.id)}
                                >
                                    {s.title}
                                </button>
                            ))}

                            <img
                                className={styles.menuLogo}
                                src={Logo}
                                alt="Logo Links Vibe"
                                loading="lazy"
                                draggable={false}
                            />
                        </div>
                    ) : (
                        <div className={styles.single}>
                            <button
                                type="button"
                                className={styles.backInline}
                                onClick={() => setSelectedServiceId(null)}
                            >
                                ← Voltar para lista
                            </button>

                            <div className={styles.grid}>
                                {servicosByanca
                                    .filter((s) => s.id === selectedServiceId)
                                    .map((s) => {
                                        const count = s.images.length;
                                        const index = getSlide(s.id, count);
                                        const goPrev = () =>
                                            setSlide(s.id, count, index === 0 ? count - 1 : index - 1);
                                        const goNext = () =>
                                            setSlide(s.id, count, index === count - 1 ? 0 : index + 1);

                                        const hasMultiple = count > 1;

                                        return (
                                            <div key={s.id} className={styles.card}>
                                                <div className={styles.carousel}>
                                                    <div className={styles.viewport}>
                                                        <div
                                                            className={styles.track}
                                                            style={{ transform: `translateX(-${index * 100}%)` }}
                                                        >
                                                            {s.images.map((src, i) => {
                                                                const slideKey = `${s.id}-${i}`;
                                                                const panPx = panPxByKey[slideKey] ?? 0;
                                                                const isActiveSlide = i === index;
                                                                const shouldAnimate =
                                                                    isActiveSlide &&
                                                                    panPx > 0 &&
                                                                    !reduceMotion;
                                                                const panKf = buildPanAnimationKeyframes(slideKey, panPx);

                                                                const innerClass = [
                                                                    styles.autoScrollInner,
                                                                    shouldAnimate && styles.autoScrollInnerAnimated,
                                                                    panPx > 0 && !shouldAnimate && styles.autoScrollInnerIdle,
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(' ');

                                                                return (
                                                                    <div
                                                                        key={slideKey}
                                                                        className={styles.slidePan}
                                                                        data-pan-slide
                                                                        data-pan-key={slideKey}
                                                                    >
                                                                        {panKf && <style>{panKf.css}</style>}
                                                                        <div
                                                                            key={`${slideKey}-${isActiveSlide ? 'on' : 'off'}`}
                                                                            className={innerClass}
                                                                            style={
                                                                                shouldAnimate && panKf
                                                                                    ? {
                                                                                        animation: `${panKf.name} ${panKf.totalSec}s linear infinite`,
                                                                                    }
                                                                                    : undefined
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={src}
                                                                                alt={`${s.title} — imagem ${i + 1}`}
                                                                                loading="lazy"
                                                                                draggable={false}
                                                                                onLoad={(e) =>
                                                                                    measurePan(slideKey, e.currentTarget)
                                                                                }
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    {hasMultiple && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className={`${styles.navBtn} ${styles.navPrev}`}
                                                                onClick={goPrev}
                                                                aria-label="Imagem anterior"
                                                            >
                                                                ‹
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`${styles.navBtn} ${styles.navNext}`}
                                                                onClick={goNext}
                                                                aria-label="Próxima imagem"
                                                            >
                                                                ›
                                                            </button>
                                                            <div
                                                                className={styles.dots}
                                                                role="tablist"
                                                                aria-label="Indicadores do carrossel"
                                                            >
                                                                {s.images.map((_, i) => (
                                                                    <button
                                                                        key={i}
                                                                        type="button"
                                                                        role="tab"
                                                                        aria-selected={i === index}
                                                                        className={`${styles.dot} ${i === index ? styles.dotActive : ''}`}
                                                                        onClick={() => setSlide(s.id, count, i)}
                                                                        aria-label={`Ir para imagem ${i + 1}`}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                <div className={styles.body}>
                                                    <h2 className={styles.title}>{s.title}</h2>
                                                    <p className={styles.description}>{s.description}</p>
                                                    <a
                                                        href={buildServicoWhatsAppUrl(
                                                            SERVICOS_BYANCA_WHATSAPP_NUMBER,
                                                            s.whatsappMessage
                                                        )}
                                                        className={styles.cta}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Saiba mais
                                                    </a>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Servicos_byancasilvaa_;
